import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { TouchableOpacity, View, Text, StyleSheet, ActivityIndicator, TextInput } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { FontAwesome5 } from '@expo/vector-icons';
import { BasicLayout, CommonContainer } from '../../../../../common/components/CustomLayout';
import { BOHButton, BOHToolbar } from '../../../../../common/components/bohtoolbar';
import AddVRPriceModal from '../modals/AddVRPriceModal';
import { getAPICall, postAPICall } from '../../../../../api/BaseAPI';
import { getBrandsList } from '../../../../../api/Price';
import { getProductCategoriesData } from '../../../../../api/Product';
import { useAlertModal } from '../../../../../common/hooks';
import { ScrollView } from 'react-native-gesture-handler';

// ── Types ──
interface PricingEntry {
  id: number;
  brand_ids: number[] | string;
  category_id: number;
  family_id: number;
  fulfillment_type: string;
  duration_type: string;
  duration_value: number;
  duration_label: string;
  price: string | number;
  miles: number | null;
  battery_percent: number | null;
  family?: { display_name: string };
  category?: { category: string };
}

interface PricingGroup {
  key: string;
  family_id: number;
  category_id: number;
  family_name: string;
  category_name: string;
  brand_ids: number[];
  brand_names: string;
  entries: PricingEntry[];
}

interface CategorySection {
  category_id: number;
  category_name: string;
  brand_ids: number[];
  brand_names: string;
  groups: PricingGroup[];
  repGroup: PricingGroup;
}

interface DurationColumn {
  key: string;
  duration_type: string;
  duration_value: number;
  label: string;
}

// ── Helpers ──
const parseBrandIds = (brandIds: any): number[] => {
  if (!brandIds) return [];
  if (Array.isArray(brandIds)) return brandIds;
  if (typeof brandIds === 'string') {
    try { return JSON.parse(brandIds); } catch { return []; }
  }
  return [];
};

const DURATION_ORDER: Record<string, number> = { hourly: 1, daily: 2, weekly: 3, monthly: 4 };
const makeDurationKey = (type: string, value: number) => `${type}_${value}`;
// daily_0 is the 'Extra Day' sentinel: the per-day surcharge beyond the largest tier.
const isExtraDayCol = (type: string, value: number) => type === 'daily' && Number(value) === 0;
const makeDurationLabel = (type: string, value: number) => {
  if (isExtraDayCol(type, value)) return 'Extra Day';
  switch (type) {
    case 'hourly': return `${value} Hour(s)`;
    case 'daily': return value === 1 ? '1 Day' : `${value} Days`;
    case 'weekly': return value === 1 ? '1 Week' : `${value} Weeks`;
    case 'monthly': return value === 1 ? '1 Month' : `${value} Months`;
    default: return `${value}`;
  }
};
const sortDurationColumns = (cols: DurationColumn[]) =>
  [...cols].sort((a, b) => {
    // Extra Day always sorts last — it's a surcharge, not a tier.
    const xA = isExtraDayCol(a.duration_type, a.duration_value) ? 1 : 0;
    const xB = isExtraDayCol(b.duration_type, b.duration_value) ? 1 : 0;
    if (xA !== xB) return xA - xB;
    const dA = DURATION_ORDER[a.duration_type] || 5;
    const dB = DURATION_ORDER[b.duration_type] || 5;
    if (dA !== dB) return dA - dB;
    return a.duration_value - b.duration_value;
  });

// ── Main Component ──
const Prices = ({ navigation, goBack }) => {
  const { showAlert } = useAlertModal();
  const [tableData, setTableData] = useState<PricingEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddModalVisible, setAddModalVisible] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<PricingGroup | null>(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [brands, setBrands] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);

  const [editingPrices, setEditingPrices] = useState<Record<number, string>>({});
  const saveTimeouts = useRef<Record<number, any>>({});

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([
        new Promise<void>((resolve) => fetchBrands(resolve)),
        new Promise<void>((resolve) => fetchCategories(resolve)),
      ]);
      await fetchVRPrices();
    };
    loadData();
  }, []);

  const fetchBrands = (resolve?: () => void) => {
    getBrandsList({}, (jsonRes, status) => {
      if (status === 200 && jsonRes) setBrands(jsonRes);
      resolve?.();
    });
  };

  const fetchCategories = (resolve?: () => void) => {
    getProductCategoriesData((jsonRes, status) => {
      if (status === 200 && jsonRes) setCategories(jsonRes);
      resolve?.();
    });
  };

  const getBrandNames = useCallback((brandIds: any) => {
    const ids = parseBrandIds(brandIds);
    return ids.map(id => {
      const brand = brands.find(b => b.id === id);
      return brand ? brand.brand : `Brand ${id}`;
    }).join(', ');
  }, [brands]);

  const fetchVRPrices = async () => {
    setIsLoading(true);
    await getAPICall('vr/prices', (jsonRes, status, error) => {
      if (status === 200 && jsonRes?.success) {
        setTableData(jsonRes.data || []);
      } else {
        showAlert('Error', error || 'Failed to fetch price data');
      }
      setIsLoading(false);
    });
  };

  // ── CRUD ──
  const createPricing = async (pricing: any) => {
    return new Promise<boolean>((resolve) => {
      postAPICall('vr/prices/create', pricing, (jsonRes, status, error) => {
        if (status === 201 && jsonRes?.success) resolve(true);
        else { showAlert('Error', jsonRes?.message || error || 'Failed to create'); resolve(false); }
      });
    });
  };

  const updatePricing = async (pricing: any) => {
    return new Promise<boolean>((resolve) => {
      postAPICall('vr/prices/update', pricing, (jsonRes, status, error) => {
        if (status === 200 && jsonRes?.success) resolve(true);
        else { showAlert('Error', jsonRes?.message || error || 'Failed to update'); resolve(false); }
      });
    });
  };

  const deletePricing = async (id: number) => {
    return new Promise<boolean>((resolve) => {
      postAPICall('vr/prices/delete', { id }, (jsonRes, status, error) => {
        if (status === 200 && jsonRes?.success) resolve(true);
        else { showAlert('Error', jsonRes?.message || error || 'Failed to delete'); resolve(false); }
      });
    });
  };

  const handlePriceChange = (entryId: number, value: string) => {
    setEditingPrices(prev => ({ ...prev, [entryId]: value }));
    if (saveTimeouts.current[entryId]) clearTimeout(saveTimeouts.current[entryId]);
    saveTimeouts.current[entryId] = setTimeout(() => {
      const entry = tableData.find(e => e.id === entryId);
      if (entry && value && parseFloat(value) > 0) {
        updatePricing({ id: entryId, price: parseFloat(value) }).then((ok) => {
          if (ok) {
            setTableData(prev => prev.map(e => e.id === entryId ? { ...e, price: value } : e));
            setEditingPrices(prev => { const n = { ...prev }; delete n[entryId]; return n; });
          }
        });
      }
    }, 1000);
  };

  // ── Build category sections: one card per unique (category + sorted brand_ids) ──
  const categorySections = useMemo((): CategorySection[] => {
    const filtered = tableData.filter(item => {
      const catMatch = !filterCategory || item.category_id === parseInt(filterCategory);
      const brandMatch = !filterBrand || parseBrandIds(item.brand_ids).includes(parseInt(filterBrand));
      return catMatch && brandMatch;
    });

    // Group by category_id + sorted brand_ids string
    const sectionMap: Record<string, CategorySection> = {};
    for (const item of filtered) {
      const brandIds = parseBrandIds(item.brand_ids);
      const brandKey = [...brandIds].sort((a, b) => a - b).join(',');
      const sectionKey = `${item.category_id}__${brandKey}`;

      if (!sectionMap[sectionKey]) {
        const repGroup: PricingGroup = {
          key: sectionKey,
          family_id: 0,
          category_id: item.category_id,
          family_name: '',
          category_name: item.category?.category || `Category ${item.category_id}`,
          brand_ids: brandIds,
          brand_names: '',
          entries: [],
        };
        sectionMap[sectionKey] = {
          category_id: item.category_id,
          category_name: item.category?.category || `Category ${item.category_id}`,
          brand_ids: brandIds,
          brand_names: '',
          groups: [repGroup],
          repGroup,
        };
      }
      sectionMap[sectionKey].repGroup.entries.push(item);
    }

    for (const s of Object.values(sectionMap)) {
      s.brand_names = getBrandNames(s.brand_ids);
      s.repGroup.brand_names = s.brand_names;
    }

    return Object.values(sectionMap)
      .sort((a, b) => {
        const catCmp = a.category_name.localeCompare(b.category_name);
        if (catCmp !== 0) return catCmp;
        return a.brand_names.localeCompare(b.brand_names);
      });
  }, [tableData, brands, filterCategory, filterBrand]);

  // ── All unique duration columns ──
  const allDurationColumns = useMemo(() => {
    const colMap: Record<string, DurationColumn> = {};
    for (const entry of tableData) {
      const key = makeDurationKey(entry.duration_type, entry.duration_value);
      if (!colMap[key]) {
        colMap[key] = {
          key,
          duration_type: entry.duration_type,
          duration_value: entry.duration_value,
          label: entry.duration_label || makeDurationLabel(entry.duration_type, entry.duration_value),
        };
      }
    }
    return sortDurationColumns(Object.values(colMap));
  }, [tableData]);

  const openAddNewGroup = () => {
    setSelectedGroup(null);
    setAddModalVisible(true);
  };

  const openEditGroup = (group: PricingGroup) => {
    setSelectedGroup(group);
    setAddModalVisible(true);
  };

  const handleModalSave = async (actions: { creates: any[]; updates: any[]; deletes: number[] }) => {
    setIsLoading(true);
    let allOk = true;
    for (const id of actions.deletes) { if (!(await deletePricing(id))) allOk = false; }
    for (const entry of actions.updates) { if (!(await updatePricing(entry))) allOk = false; }
    for (const entry of actions.creates) { if (!(await createPricing(entry))) allOk = false; }
    if (allOk) showAlert('Success', 'Pricing saved successfully');
    await fetchVRPrices();
    setIsLoading(false);
    setAddModalVisible(false);
  };

  // ── Render pricing grid for a group ──
  const renderPricingGrid = (group: PricingGroup) => {
    const groupColKeys = new Set(group.entries.map(e => makeDurationKey(e.duration_type, e.duration_value)));
    const columns = allDurationColumns.filter(c => groupColKeys.has(c.key));

    const lookup: Record<string, Record<string, PricingEntry>> = {};
    for (const entry of group.entries) {
      const dKey = makeDurationKey(entry.duration_type, entry.duration_value);
      if (!lookup[entry.fulfillment_type]) lookup[entry.fulfillment_type] = {};
      const existing = lookup[entry.fulfillment_type][dKey];
      if (!existing || entry.id > existing.id) {
        lookup[entry.fulfillment_type][dKey] = entry;
      }
    }

    const fulfillmentTypes = ['Pickup', 'Delivery'].filter(ft =>
      group.entries.some(e => e.fulfillment_type === ft)
    );

    const COL_W = 85;
    const LABEL_W = 90;

    return (
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 0 }}>
        <View>
          <View style={styles.gridHeaderRow}>
            <View style={[styles.gridLabelCell, { width: LABEL_W }]}>
              <Text style={styles.gridHeaderText}>Fulfillment</Text>
            </View>
            {columns.map(col => (
              <View key={col.key} style={[styles.gridHeaderCell, { width: COL_W }]}>
                <Text style={styles.gridHeaderText}>{col.label}</Text>
              </View>
            ))}
          </View>
          {fulfillmentTypes.map((ft, idx) => (
            <View key={ft} style={[styles.gridDataRow, idx % 2 === 0 ? styles.gridRowEven : styles.gridRowOdd]}>
              <View style={[styles.gridLabelCell, { width: LABEL_W }]}>
                <View style={[styles.ftBadge, ft === 'Pickup' ? styles.ftPickup : styles.ftDelivery]}>
                  <Text style={styles.ftBadgeText}>{ft}</Text>
                </View>
              </View>
              {columns.map(col => {
                const entry = lookup[ft]?.[col.key];
                if (!entry) {
                  return (
                    <View key={col.key} style={[styles.gridCell, { width: COL_W }]}>
                      <Text style={styles.gridCellEmpty}>—</Text>
                    </View>
                  );
                }
                const displayPrice = editingPrices[entry.id] !== undefined
                  ? editingPrices[entry.id]
                  : parseFloat(entry.price as string).toFixed(0);
                return (
                  <View key={col.key} style={[styles.gridCell, { width: COL_W }]}>
                    <TextInput
                      style={styles.gridPriceInput}
                      value={String(displayPrice)}
                      onChangeText={(text) => handlePriceChange(entry.id, text)}
                      keyboardType="decimal-pad"
                      selectTextOnFocus
                    />
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      </ScrollView>
    );
  };

  // ── Render ──
  return (
    <BasicLayout navigation={navigation} goBack={goBack} screenName={'Prices'}>
      <CommonContainer>
        <BOHToolbar>
          <BOHButton label="+ Add Pricing Group" onPress={openAddNewGroup} />
          <View style={styles.filterContainer}>
            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>Category:</Text>
              <Picker
                selectedValue={filterCategory}
                onValueChange={(value) => setFilterCategory(value)}
                style={styles.filterPicker}
              >
                <Picker.Item label="All Categories" value="" />
                {categories.map((c) => (
                  <Picker.Item key={c.id} label={c.category} value={c.id.toString()} />
                ))}
              </Picker>
            </View>
            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>Brand:</Text>
              <Picker
                selectedValue={filterBrand}
                onValueChange={(value) => setFilterBrand(value)}
                style={styles.filterPicker}
              >
                <Picker.Item label="All Brands" value="" />
                {brands.map((b) => (
                  <Picker.Item key={b.id} label={b.brand} value={b.id.toString()} />
                ))}
              </Picker>
            </View>
            {(filterCategory || filterBrand) && (
              <TouchableOpacity
                onPress={() => { setFilterCategory(''); setFilterBrand(''); }}
                style={styles.clearButton}
              >
                <Text style={styles.clearButtonText}>Clear Filters</Text>
              </TouchableOpacity>
            )}
          </View>
        </BOHToolbar>

        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#2e96e1" />
          </View>
        ) : (
          <ScrollView style={{ flex: 1 }}>
            {categorySections.length === 0 && (
              <View style={styles.emptyState}>
                <Text style={styles.emptyText}>No pricing groups found. Click "+ Add Pricing Group" to create one.</Text>
              </View>
            )}
            {categorySections.map((section) => {
              const sectionKey = `${section.category_id}__${[...section.brand_ids].sort((a,b)=>a-b).join(',')}`;
              return (
                <View key={sectionKey} style={styles.categoryCard}>

                  {/* ── Card header ── */}
                  <View style={styles.categoryHeader}>
                    <View style={styles.categoryHeaderTop}>
                      <Text style={styles.categoryTitle}>{section.category_name}</Text>
                      <View style={styles.brandTagsContainer}>
                        {section.brand_names
                          ? section.brand_names.split(', ').map((name, i) => (
                              <View key={i} style={styles.brandTag}>
                                <Text style={styles.brandTagText}>{name}</Text>
                              </View>
                            ))
                          : <Text style={styles.nobrands}>No brands</Text>
                        }
                      </View>
                      <TouchableOpacity
                        onPress={() => openEditGroup(section.repGroup)}
                        style={styles.editBtn}
                      >
                        <FontAwesome5 name="edit" size={12} color="#2e96e1" />
                        <Text style={styles.editBtnText}>Edit</Text>
                      </TouchableOpacity>
                    </View>
                  </View>

                  {/* ── Pricing grid ── */}
                  <View style={styles.gridWrapper}>
                    {renderPricingGrid(section.repGroup)}
                  </View>

                </View>
              );
            })}
          </ScrollView>
        )}

        <AddVRPriceModal
          isModalVisible={isAddModalVisible}
          group={selectedGroup}
          closeModal={() => setAddModalVisible(false)}
          existingData={tableData}
          brands={brands}
          onSave={handleModalSave}
        />
      </CommonContainer>
    </BasicLayout>
  );
};

const styles = StyleSheet.create({
  filterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
    marginLeft: 20,
    flex: 1,
  },
  filterItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: '600',
  },
  filterPicker: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    padding: 8,
    minWidth: 180,
    height: 40,
  },
  clearButton: {
    backgroundColor: '#f44336',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 4,
  },
  clearButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyState: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 15,
    color: '#888',
  },
  // ── Category Card ──
  categoryCard: {
    borderWidth: 1,
    borderColor: '#d0dce8',
    borderRadius: 8,
    marginBottom: 16,
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  categoryHeader: {
    backgroundColor: '#e8f4f8',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#b8d4e8',
  },
  categoryHeaderTop: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  categoryTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1a3a5c',
    marginRight: 4,
  },
  brandTagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
  },
  brandTag: {
    backgroundColor: '#e3f2fd',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#2196f3',
  },
  brandTagText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#1565c0',
  },
  nobrands: {
    fontSize: 12,
    color: '#aaa',
    fontStyle: 'italic',
  },
  gridWrapper: {
    paddingVertical: 4,
  },
  editBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginLeft: 'auto',
    backgroundColor: '#e8f4fd',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#2e96e1',
  },
  editBtnText: {
    fontSize: 12,
    color: '#2e96e1',
    fontWeight: '600',
  },
  // ── Families toggle ──
  familiesToggleBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderTopWidth: 1,
    borderTopColor: '#e8eef4',
    backgroundColor: '#f8fbff',
  },
  familiesToggleText: {
    fontSize: 13,
    color: '#2e96e1',
    fontWeight: '600',
  },
  familiesList: {
    borderTopWidth: 1,
    borderTopColor: '#e8eef4',
  },
  familyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 9,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f4f8',
  },
  familyRowName: {
    fontSize: 13,
    color: '#333',
    fontWeight: '500',
    flex: 1,
  },
  manageBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#e8f4fd',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#2e96e1',
  },
  manageBtnText: {
    fontSize: 12,
    color: '#2e96e1',
    fontWeight: '600',
  },
  // ── Pricing Grid ──
  gridHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: '#d0d0d0',
    backgroundColor: '#f5f7fa',
  },
  gridLabelCell: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    justifyContent: 'center',
  },
  gridHeaderCell: {
    paddingVertical: 6,
    paddingHorizontal: 3,
    alignItems: 'center',
    justifyContent: 'center',
    borderLeftWidth: 1,
    borderLeftColor: '#e0e0e0',
  },
  gridHeaderText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#444',
    textAlign: 'center',
  },
  gridDataRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  gridRowEven: { backgroundColor: '#fff' },
  gridRowOdd: { backgroundColor: '#fafbfc' },
  gridCell: {
    paddingVertical: 4,
    paddingHorizontal: 3,
    alignItems: 'center',
    justifyContent: 'center',
    borderLeftWidth: 1,
    borderLeftColor: '#f0f0f0',
  },
  gridPriceInput: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    color: '#1a1a1a',
    width: '100%',
    height: 32,
    borderWidth: 1,
    borderColor: '#d8d8d8',
    borderRadius: 4,
    backgroundColor: '#fff',
    paddingHorizontal: 6,
  },
  gridCellEmpty: {
    fontSize: 13,
    color: '#ccc',
  },
  ftBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
    minWidth: 70,
    alignItems: 'center',
  },
  ftPickup: { backgroundColor: '#e8f5e9' },
  ftDelivery: { backgroundColor: '#fff3e0' },
  ftBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#2c3e50',
  },
});

export default Prices;
